import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./discover.css";
import { useState, useEffect, useContext } from "react";
import generalRequest from "../../request/generalRequest";
import { CombinedContext } from "../../context/combinedContext";
import BookingModal from "../bookingModal/bookingModal";
import translations from "../../translations/translations";
import DiscoverSkeleton from "./discoverSkeleton/discoverSkeleton";

const Discover = () => {
	// 📍 Browser location
	const [coords, setCoords] = useState(null);

	const [sortBy, setSortBy] = useState("rating_high");
	const [isSortOpen, setIsSortOpen] = useState(false);

	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);

	// 🔹 Booking modal state
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [selectedShop, setSelectedShop] = useState(null);

	// 🔹 Context
	const { language, selectedService, setSelectedService, selectedstaff, category, setCategory, shopsData, setShopsData } = useContext(CombinedContext);

	const { mainCategory, subCategory } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	useEffect(() => {
		const q = searchParams.get("q");
		const loc = searchParams.get("loc");

		if (!q && !loc) return;

		async function searchFromParams() {
			try {
				setLoading(true);
				const data = await generalRequest.searchShops(q || "", 1, 20, loc || undefined);
				setShopsData(data.shops);
			} catch (err) {
				setError(err?.message || String(err));
			} finally {
				setLoading(false);
			}
		}

		searchFromParams();
	}, [searchParams]);

	/* ---------------- Category sync ---------------- */

	useEffect(() => {
		if (!mainCategory) {
			navigate("/barbershop/barbershop");
		} else {
			setCategory(subCategory || mainCategory);
		}
	}, [mainCategory, subCategory]);

	/* ---------------- Request browser location ---------------- */

	useEffect(() => {
		const DEFAULT_COORDS = {
			lat: 18.4861,
			lng: -69.9312, // Santo Domingo
		};

		if (!navigator.geolocation) {
			setCoords(DEFAULT_COORDS);
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(pos) => {
				setCoords({
					lat: pos.coords.latitude,
					lng: pos.coords.longitude,
				});
			},
			() => {
				// User denied or error → fallback to Santo Domingo
				setCoords(DEFAULT_COORDS);
			},
			{
				enableHighAccuracy: true,
				timeout: 8000,
				maximumAge: 60_000,
			},
		);
	}, []);

	/* ---------------- Load shops ---------------- */

	useEffect(() => {
		if (!coords) return;
		setLoading(true);

		async function loadShops() {
			try {
				const data = await generalRequest.discover({
					category: mainCategory,
					subCategory: subCategory || "all",
					sortBy,
					page: 1,
					limit: 20,
					lat: coords?.lat,
					lng: coords?.lng,
				});
				setShopsData(data.shops || []);
			} catch (err) {
				setError(err?.message || String(err));
			} finally {
				setLoading(false);
			}
		}

		loadShops();
	}, [mainCategory, subCategory, sortBy, coords]);

	/* ---------------- Helpers ---------------- */

	const getImageUrl = (img) => {
		if (!img) return "https://via.placeholder.com/900x500?text=No+Image";
		return img.startsWith("http") ? img : `https://${process.env.REACT_APP_IMAGES_BUCKET}.s3.${process.env.REACT_APP_APP_REGION}.amazonaws.com/${img}`;
	};

	const handleBookService = (shop, serviceId) => {
		setSelectedService(serviceId);
		setSelectedShop(shop);
		setIsBookingOpen(true);
	};

	/* ---------------- Render states ---------------- */

	if (loading) return <DiscoverSkeleton />;
	if (error) return <p className="sf-error">{error}</p>;

	return (
		<div className="sf-discover-page">
			<div className="sf-spacer" />

			{/* Toolbar */}
			<div className="sf-toolbar">
				<button className="sf-selectbtn" type="button" onClick={() => navigate("/map")}>
					{translations[language].discover.toolbar.showMap}
				</button>

				<div className="sf-sort">
					<button className="sf-selectbtn" type="button" onClick={() => setIsSortOpen((v) => !v)}>
						{translations[language].discover.toolbar.sortBy}{" "}
						<b>
							{sortBy === "distance"
								? translations[language].discover.sort.distance
								: sortBy === "rating_high"
									? translations[language].discover.sort.highestRated
									: translations[language].discover.sort.recommended}
						</b>{" "}
						▾
					</button>

					{isSortOpen && (
						<div className="sf-sort-dropdown">
							<button
								onClick={() => {
									setSortBy("recommended");
									setIsSortOpen(false);
								}}
							>
								{translations[language].discover.sort.recommended}
							</button>
							<button
								onClick={() => {
									setSortBy("distance");
									setIsSortOpen(false);
								}}
							>
								{translations[language].discover.sort.distance}
							</button>

							<button
								onClick={() => {
									setSortBy("rating_high");
									setIsSortOpen(false);
								}}
							>
								{translations[language].discover.sort.highestRated}
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Title */}
			<div className="sf-titleblock">
				<h1 className="sf-title">
					{translations[language].discover.title} <span className="sf-count">({shopsData.length})</span>
				</h1>
			</div>

			{/* Results */}
			<section className="sf-results">
				{shopsData.map((shop) => {
					const rating = Number(shop.rating || 0).toFixed(1);
					const reviewCount = shop.totalReviews || 0;
					const img = shop.logo || shop.images?.[0];

					return (
						<article className="sf-row" key={shop.shopId}>
							<Link to={`/shops/${shop.shopId}`} className="sf-shopname">
								<div className="sf-row__media">
									<img className="sf-img" src={getImageUrl(img)} alt={shop.name} />
									<div className="sf-ratingpill">
										<span className="sf-ratingpill__score">{rating}</span>
										<span className="sf-ratingpill__sub">
											{reviewCount} {translations[language].discover.reviews}
										</span>
									</div>
								</div>
							</Link>

							<div className="sf-row__body">
								<header className="sf-row__head">
									<Link to={`/shops/${shop.shopId}`} className="sf-shopname">
										{shop.name}
									</Link>
									<div className="sf-address">
										{shop.address}
										{shop.city ? `, ${shop.city}` : ""}
									</div>
								</header>
								{/* Services */}
								{shop.services.length > 0 && (
									<div className="sf-services">
										<div className="sf-services">
											{shop.services.slice(0, 3).map((service, idx) => (
												<div className="sf-service-row" key={service?.serviceId || idx}>
													<div className="sf-service-divider" aria-hidden="true" />
													<div className="sf-service-item">
														<div className="sf-service-info">
															<div className="sf-service-name">
																{service.name}{" "}
																{(() => {
																	const loc = service.locationType || (service.isHomeService ? "home" : "in_shop");
																	if (loc === "home") {
																		return (
																			<span className="sf-shop-service-home">
																				<span className="sf-shop-dot"> · </span>
																				{language === "es" ? "A domicilio" : "Home service"}
																			</span>
																		);
																	}
																	if (loc === "online") {
																		return (
																			<span className="sf-shop-service-home">
																				<span className="sf-shop-dot"> · </span>
																				{language === "es" ? "En línea" : "Online"}
																			</span>
																		);
																	}
																	return null;
																})()}
															</div>
															<div className="sf-service-duration">{service.description}</div>
														</div>
														<div className="sf-service-right-discover">
															<div className="sf-service-price-and-duration">
																<div className="sf-service-price">
																	{service.currency || "USD"} {service.price ? Number(service.price).toFixed(2) : "—"}
																</div>
																{service.duration && <div className="sf-service-duration">{service.duration} min</div>}
															</div>
															<button
																type="button"
																className="sf-service-book-btn"
																onClick={() => handleBookService(shop, service.serviceId)}
															>
																{translations[language].discover.services.book}
															</button>
														</div>
													</div>
													<div className="sf-service-divider" aria-hidden="true" />
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</article>
					);
				})}

				{shopsData.length === 0 && <div className="sf-empty">{translations[language].discover.empty}</div>}
			</section>

			{/* Booking Modal */}
			{isBookingOpen && selectedShop && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => setIsBookingOpen(false)}
					availabilityId={selectedShop.shopId}
					shopData={selectedShop}
					initialServiceId={selectedService}
					initialstaffId={selectedstaff}
				/>
			)}
		</div>
	);
};

export default Discover;
